import { httpClient } from './http-client';

export type FlowAccountType = 'Asset' | 'Debt' | 'Investment' | 'Income' | 'Expense' | 'Equity';

export interface FlowNode {
  account_id: string;
  name: string;
  account_type: FlowAccountType;
  currency: string;
  inflow: string;
  outflow: string;
  volume: string;
  balance: string;
}

export interface FlowEdge {
  source: string;
  target: string;
  value: string;
  count: number;
}

export interface FlowPeriod {
  period: string;
  label: string;
  nodes: FlowNode[];
  edges: FlowEdge[];
  total_in: string;
  total_out: string;
}

export interface FlowResponse {
  from: string;
  to: string;
  periods: FlowPeriod[];
}

export interface AccountEntry {
  entry_id: string;
  date: string;
  description: string;
  amount: string;
  counterparty: string;
  source: string;
}

export interface AccountFlowDetail {
  account_id: string;
  name: string;
  account_type: FlowAccountType;
  period: string;
  inflow: string;
  outflow: string;
  balance: string;
  entries: AccountEntry[];
}

/** Persisted arrangement of the graph. */
export interface FlowView {
  id: string;
  name: string;
  layout_mode: 'column' | 'force' | 'sankey';
  node_positions: Record<string, { x: number; y: number }>;
  hidden_accounts: string[];
  color_overrides: Record<string, string>;
  viewport: { x: number; y: number; k: number };
  active_period?: string | null;
  is_default: boolean;
}

export type FlowViewInput = Omit<FlowView, 'id'> & { id?: string };

class FlowService {
  private static readonly BASE = '/flow';

  /** Fetch the month-bucketed flow graph. Dates are `YYYY-MM-DD`. */
  static async getFlow(from?: string, to?: string): Promise<FlowResponse> {
    const qs = new URLSearchParams();
    if (from) qs.set('from', from);
    if (to) qs.set('to', to);
    const suffix = qs.toString() ? `?${qs}` : '';
    return httpClient.get<FlowResponse>(`${FlowService.BASE}${suffix}`);
  }

  /** Fetch one account's activity for the drill-down panel. */
  static async getAccountDetail(
    accountId: string, from: string, to: string,
  ): Promise<AccountFlowDetail> {
    return httpClient.get<AccountFlowDetail>(
      `${FlowService.BASE}/accounts/${accountId}?from=${from}&to=${to}`,
    );
  }

  static async listViews(): Promise<FlowView[]> {
    const res = await httpClient.get<{ views: FlowView[] }>(`${FlowService.BASE}/views`);
    return res.views ?? [];
  }

  /** Create or overwrite a view. Saving under an existing name replaces it. */
  static async saveView(view: FlowViewInput): Promise<FlowView> {
    if (view.id) {
      return httpClient.put<FlowView>(`${FlowService.BASE}/views/${view.id}`, view);
    }
    return httpClient.post<FlowView>(`${FlowService.BASE}/views`, view);
  }

  static async deleteView(id: string): Promise<void> {
    await httpClient.delete(`${FlowService.BASE}/views/${id}`);
  }
}

export const flowService = FlowService;
